/**
 * UTM Parameter Capture Utility
 *
 * Captures UTM parameters from the current URL on page load and persists
 * them in sessionStorage so they survive SPA navigation and can be attached
 * to registration payloads for attribution.
 *
 * GA4 auto-reads UTM params from the landing URL, so this utility focuses
 * on making them available to application code (e.g., registration forms).
 *
 * Supported params: utm_source, utm_medium, utm_campaign, utm_content, utm_term
 */

const UTM_STORAGE_KEY = 'rxr_utm';

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

/**
 * Read UTM parameters from the current URL and store them in sessionStorage.
 * Only overwrites if at least one UTM param is present (first-touch per session).
 * Call this once on app mount.
 */
export function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    let hasAny = false;

    UTM_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        // Strip the 'utm_' prefix for cleaner storage
        utm[key.replace('utm_', '')] = value;
        hasAny = true;
      }
    });

    if (hasAny) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
  } catch (err) {
    // sessionStorage may be unavailable in private browsing / iframe contexts
    console.warn('UTM capture failed:', err);
  }
}

/**
 * Retrieve previously captured UTM parameters.
 *
 * @returns {{ source?: string, medium?: string, campaign?: string, content?: string, term?: string } | null}
 */
export function getUtmParams() {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Retrieve UTM parameters with the `utm_` prefix preserved, suitable for
 * the backend's `signupUtmParams` field (RegisterDirectDto / RegisterUserDto).
 *
 * Reads first from sessionStorage (first-touch captured on app mount) and
 * re-applies the `utm_` prefix; falls back to the current URL query string.
 *
 * @returns {Record<string, string>} — empty object when no params found.
 */
export function getSignupUtmParams() {
  const out = {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) || {};
      UTM_PARAMS.forEach((key) => {
        const short = key.replace('utm_', '');
        if (stored[short]) {
          out[key] = stored[short];
        }
      });
      if (Object.keys(out).length > 0) return out;
    }
  } catch {
    // fall through to URL parse below
  }
  try {
    const params = new URLSearchParams(window.location.search);
    UTM_PARAMS.forEach((key) => {
      const v = params.get(key);
      if (v) out[key] = v;
    });
  } catch {
    // ignore
  }
  return out;
}

/**
 * Source options for the "How did you hear about us?" dropdown.
 * Keep values in sync with backend `SignupSource` enum
 * (`backend/src/marketing-tracker/schemas/signup-source.enum.ts`).
 */
export const SIGNUP_SOURCE_OPTIONS = [
  { value: 'instagram',        label: 'Instagram' },
  { value: 'facebook_group',   label: 'Facebook group' },
  { value: 'crna_referral',    label: 'Referred by another CRNA' },
  { value: 'podcast_press',    label: 'Podcast / press / publication' },
  { value: 'email_mail',       label: 'Email / direct mail' },
  { value: 'conference_event', label: 'Conference / event' },
  { value: 'other',            label: 'Other' },
];

/**
 * Map of utm_source values to canonical SignupSource enum values.
 * Used to pre-select the dropdown when a matching UTM is present.
 */
const UTM_TO_SIGNUP_SOURCE = {
  ig: 'instagram',
  instagram: 'instagram',
  fb: 'facebook_group',
  facebook: 'facebook_group',
  email: 'email_mail',
  mailchimp: 'email_mail',
  sendgrid: 'email_mail',
  podcast: 'podcast_press',
  press: 'podcast_press',
  aana: 'podcast_press',
  conference: 'conference_event',
  event: 'conference_event',
};

/**
 * Given a `utm_source` string, return the matching SignupSource option value
 * if we recognize it, otherwise ''. Conference/event heuristic: any `*-aana`
 * value (e.g. `2026-aana`) maps to `conference_event`.
 *
 * @param {string|undefined} utmSource
 * @returns {string}
 */
export function signupSourceFromUtm(utmSource) {
  if (!utmSource) return '';
  const lower = String(utmSource).toLowerCase();
  if (UTM_TO_SIGNUP_SOURCE[lower]) return UTM_TO_SIGNUP_SOURCE[lower];
  if (lower.endsWith('-aana')) return 'conference_event';
  return '';
}
